/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, OnInit} from '@angular/core';
import * as _ from 'lodash';

import template from './creative-preview-view.component.html';
import {
  collateCreativePreviewLineItemData,
  collateCreativePreviewRequestData
} from './preview-request-data';
import {PreviewResource} from '../../../components/api/resources/resource/preview.resource';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';
import {requiresBlankPagePreview, requiresVideoPreviewParam} from './preview-language';

@Component({
  standalone: false,
  selector: 'adn-creative-preview-via-layout',
  template
})
export class CreativePreviewViaLayoutComponent implements OnInit {
  @Input() previewData: any;
  @Input() creative: any;
  @Input() creativePreviewTrigger: any;
  @Input() maxWidth: string;
  @Input() maxHeight: string;

  previewTrigger: any = {};
  resolved = false;
  layoutType: string;

  private lastPreviewParams: any;

  constructor(
    @Inject(PreviewResource) private readonly Preview: any,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.runPreview(this.previewData);

    if (this.creativePreviewTrigger) {
      this.creativePreviewTrigger.previewCreative = (params: any) => {
        this.runPreview(params);
      };
    }

    this.previewTrigger.refreshPreview = (hasUserTargeting: boolean) => {
      if (!this.lastPreviewParams) {
        return;
      }
      this.runPreview(_.assign({}, this.lastPreviewParams, {
        hasUserTargetingOverride: hasUserTargeting
      }));
    };
  }

  private runPreview(params: any): void {
    this.lastPreviewParams = params ? _.omit(params, ['hasUserTargetingOverride']) : null;
    this.resolved = false;

    if (!params || (!params.layoutId && !params.renderTemplate && !params.creative)) {
      this.resolved = true;
      return;
    }

    const creative = params.creative;
    const lineItem = params.lineItem || params;
    const layoutType = _.get(creative, ['layout', 'layoutType']) || '';
    const ogLayoutType = _.get(creative, ['layout', 'ogLayoutType']) || '';
    const promises: any[] = [];

    const layoutParams: any = {};
    const totalAssets = _.get(creative, ['assets'], []).concat(_.get(creative, ['libraryCreativeAssets'], []));
    _.forEach(creative.layoutParameters, (v: any, k: string) => {
      if (v && v.id && !v.content) {
        const assetId = v.id;
        layoutParams[k] = _.pick((_.find(totalAssets, ['id', assetId]) || {}),
          ['cdnId', 'previewCdnId', 'transcode', 'width', 'height', 'content', 'duration', 'bps']);
      } else {
        layoutParams[k] = v;
      }
    });

    this.layoutType = ogLayoutType || layoutType || _.get(creative, ['layout', 'detectedCategory']);
    const apiPreviewData: any = {
      creativeWidth: creative.width || creative.height || 300,
      creativeHeight: creative.height || creative.width || 300,
      layoutParameters: layoutParams,
      video: _.isUndefined(params.video) ? requiresVideoPreviewParam(creative) : params.video
    };
    _.assign(apiPreviewData, collateCreativePreviewLineItemData(creative, lineItem));
    _.assign(apiPreviewData, collateCreativePreviewRequestData(creative, lineItem, params.advertiser, params.election));
    if (!_.isUndefined(params.hasUserTargetingOverride)) {
      apiPreviewData.hasUserTargeting = params.hasUserTargetingOverride;
    }
    if (params.layoutId) {
      apiPreviewData.layoutId = params.layoutId;
    }
    if (_.get(creative, ['layout', 'id'])) {
      apiPreviewData.layoutId = creative.layout.id;
    }
    if (params.renderTemplate) {
      apiPreviewData.renderTemplate = params.renderTemplate;
    }

    if (!apiPreviewData.layoutParameters || _.isEmpty(apiPreviewData.layoutParameters)) {
      promises.push(Promise.resolve(''));
    } else {
      promises.push(new this.Preview(apiPreviewData).$save().then((data: any) => data.document));
    }

    Promise.all(promises).then((results: any[]) => {
      const creativeContent = results[0] || '\x3Chtml>\x3Cbody>\x3C/body>\x3C/html>';
      const previewPayload = {
        pageTitle: params.renderTemplate ? 'Full-Screen Layout Preview' : 'Full-Screen Creative Preview',
        creativeContent,
        creativeWidth: creative.width || creative.height || 300,
        creativeHeight: creative.height || creative.width || 300,
        dimensionType: creative.dimensionType,
        layoutType: ogLayoutType || layoutType,
        maxWidth: this.maxWidth,
        maxHeight: this.maxHeight,
        center: true
      };

      const blankPageOnly = requiresBlankPagePreview(creative);
      deferMarkForCheck(this.cdr, () => {
        this.previewTrigger.previewInline?.(previewPayload, apiPreviewData, blankPageOnly);
        this.resolved = true;
      });
    });
  }
}
